import { createContext, useContext, useEffect, useRef } from 'react';
import styles from './index.module.scss';
import { cn } from '../lib/utils';
import Button from '../Button';
import {useMemo} from 'react';

const DialogContext = createContext(null);

function useDialog() {
    const ctx = useContext(DialogContext);
    if (!ctx) {
        throw new Error('Dialog components must be used within <Dialog />');
    }
    return ctx;
}

export function Dialog({ open, onOpenChange, children }) {
    const dialogRef = useRef(null);

    function syncDialog(dialogRef, isOpen) {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (isOpen !== dialog.open) {
            isOpen ? dialog.showModal() : dialog.close();
        }
    }

    useEffect(() => {
        syncDialog(dialogRef, open);
    }, [open]);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        const handleClose = () => onOpenChange(false);

        const handleClick = e => {
            if (e.target === dialog) {
                dialog.close();
            }
        };

        dialog.addEventListener('close', handleClose);
        dialog.addEventListener('click', handleClick);

        return () => {
            if (dialog) {
                dialog.removeEventListener('close', handleClose);
                dialog.removeEventListener('click', handleClick);
            }
        };
    }, [onOpenChange]);

    const contextValue = useMemo(
        () => ({
            dialogRef,
            onOpenChange: value => {
                syncDialog(dialogRef, value);
                onOpenChange(value);
            },
        }),
        [onOpenChange],
    );

    return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>;
}

export function DialogContent({ children, className = '' }) {
    const { dialogRef } = useDialog();

    return (
        <dialog ref={dialogRef} className={cn(styles.container, className)}>
            {children}
        </dialog>
    );
}

export function DialogHeader({ children, className = '' }) {
    return <div className={cn(styles.header, className)}>{children}</div>;
}

export function DialogTitle({ children, className = '' }) {
    return <h4 className={cn(styles.title, className)}>{children}</h4>;
}

export function DialogDescription({ children, className = '' }) {
    return <p className={cn(styles.description, className)}>{children}</p>;
}

export function DialogFooter({ children, className = '' }) {
    return <div className={cn(styles.footer, className)}>{children}</div>;
}

export function DialogClose({ children, className = '', ...props }) {
    const { onOpenChange } = useDialog();

    return (
        <Button variant='ghost' className={cn(styles.button, className)} onClick={() => onOpenChange(false)} {...props}>
            {children}
        </Button>
    );
}

export function DialogAction({ children, className = '', variant = 'primary', ...props }) {


    return (
        <Button variant={variant} className={cn(styles.button, className)} {...props}>
            {children}
        </Button>
    );
}
